import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log, error as logError } from '../utils/DebugLogger';

import '../contracts/SiteUnidades_ListaResponse';
import '../contracts/SiteUnidades_GetResponse';
import '../contracts/SiteUnidades_SalvarResponse';
import '../contracts/SiteUnidades_SalvarInput';
import '../contracts/SiteUnidades_GaleriaFotoInput';
import '../contracts/SiteUnidades_ArquivarInput';
import '../contracts/SiteUnidades_RestaurarInput';
import '../contracts/SiteUnidades_UploadFotoInput';
import '../contracts/SiteUnidades_UploadFotoResponse';
import type { SiteUnidades_SalvarInput } from '../contracts/SiteUnidades_SalvarInput';

/**
 * Composable for the "Unidades do site" staff panel (institutional website
 * units CRUD, Full-only). Mirrors useOrigens.ts.
 */
export function useSiteUnidades() {
  const isLoading = ref(false);
  const error = ref(null);

  /**
   * Fetch all units (staff view, archived included) for table listing.
   */
  const fetchUnidades = async () => {
    log('useSiteUnidades', 'fetchUnidades', 'Fetching all units');
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get('/index.php/config/site_unidades/lista', { responseContract: 'SiteUnidades_ListaResponse' });
      log('useSiteUnidades', 'fetchUnidades', 'Response', response);

      if (response.success) {
        return response.dados?.items || [];
      } else {
        throw new Error(response.error || 'Erro ao buscar unidades');
      }
    } catch (err) {
      logError('useSiteUnidades', 'fetchUnidades', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch single unit by ID.
   */
  const fetchUnidade = async (unidadeId: number) => {
    log('useSiteUnidades', 'fetchUnidade', 'Fetching unit', { unidadeId, type: typeof unidadeId });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/config/site_unidades/get/${unidadeId}`, { responseContract: 'SiteUnidades_GetResponse' });
      log('useSiteUnidades', 'fetchUnidade', 'Response', response);

      if (response.success) {
        return response.dados?.unidade;
      } else {
        const errorMsg = response.error || 'Erro ao buscar unidade';
        logError('useSiteUnidades', 'fetchUnidade', 'Backend returned error', {
          response,
          error: errorMsg
        });
        throw new Error(errorMsg);
      }
    } catch (err) {
      logError('useSiteUnidades', 'fetchUnidade', 'Error', {
        unidadeId,
        message: err.message,
        status: err.status,
        statusText: err.statusText,
        data: err.data,
        response: err.response
      });
      error.value = err.message;

      if ((err as any).data) {
        const enhancedError = new Error((err as any).data.error || (err as any).data.Error || err.message);
        (enhancedError as any).originalError = err;
        (enhancedError as any).response = (err as any).response;
        (enhancedError as any).data = (err as any).data;
        throw enhancedError;
      }

      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Create or update a unit (id present in the payload = update, absent = create).
   */
  const salvarUnidade = async (data: SiteUnidades_SalvarInput) => {
    log('useSiteUnidades', 'salvarUnidade', 'Saving unit', data);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post('/index.php/config/site_unidades/salvar', data, {
        responseContract: 'SiteUnidades_SalvarResponse',
        inputContract: 'SiteUnidades_SalvarInput'
      });
      log('useSiteUnidades', 'salvarUnidade', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao salvar unidade');
      }
    } catch (err) {
      logError('useSiteUnidades', 'salvarUnidade', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Soft-delete (archive) a unit — the public site stops showing it.
   */
  const arquivarUnidade = async (unidadeId: number) => {
    log('useSiteUnidades', 'arquivarUnidade', 'Archiving unit', unidadeId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(`/index.php/config/site_unidades/arquivar/${unidadeId}`, {}, {
        responseContract: 'SiteUnidades_SalvarResponse',
        inputContract: 'SiteUnidades_ArquivarInput'
      });
      log('useSiteUnidades', 'arquivarUnidade', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao arquivar unidade');
      }
    } catch (err) {
      logError('useSiteUnidades', 'arquivarUnidade', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Restore a previously archived unit.
   */
  const restaurarUnidade = async (unidadeId: number) => {
    log('useSiteUnidades', 'restaurarUnidade', 'Restoring unit', unidadeId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(`/index.php/config/site_unidades/restaurar/${unidadeId}`, {}, {
        responseContract: 'SiteUnidades_SalvarResponse',
        inputContract: 'SiteUnidades_RestaurarInput'
      });
      log('useSiteUnidades', 'restaurarUnidade', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao restaurar unidade');
      }
    } catch (err) {
      logError('useSiteUnidades', 'restaurarUnidade', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Hard delete a unit (Full only, irreversible).
   */
  const excluirUnidade = async (unidadeId: number) => {
    log('useSiteUnidades', 'excluirUnidade', 'Deleting unit', unidadeId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.delete(`/index.php/config/site_unidades/excluir/${unidadeId}`, {
        responseContract: 'SiteUnidades_SalvarResponse'
      });
      log('useSiteUnidades', 'excluirUnidade', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao excluir unidade');
      }
    } catch (err) {
      logError('useSiteUnidades', 'excluirUnidade', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Upload one photo (multipart, field `foto`) and return its public URL.
   * The form then puts that URL in imagem_url / galeria[].foto_url and saves.
   */
  const uploadFoto = async (file: File): Promise<string> => {
    log('useSiteUnidades', 'uploadFoto', 'Uploading photo', { name: file.name, size: file.size, type: file.type });
    isLoading.value = true;
    error.value = null;

    try {
      const formData = new FormData();
      formData.append('foto', file);

      const response = await httpService.post('/index.php/config/site_unidades/upload_foto', formData, {
        responseContract: 'SiteUnidades_UploadFotoResponse',
        inputContract: 'SiteUnidades_UploadFotoInput'
      });
      log('useSiteUnidades', 'uploadFoto', 'Response', response);

      if (response.success && response.dados) {
        return response.dados.url;
      } else {
        throw new Error(response.error || 'Erro ao enviar a foto');
      }
    } catch (err) {
      logError('useSiteUnidades', 'uploadFoto', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    // State
    isLoading,
    error,

    // CRUD operations
    fetchUnidades,
    fetchUnidade,
    salvarUnidade,
    arquivarUnidade,
    restaurarUnidade,
    excluirUnidade,
    uploadFoto
  };
}
